import {useGrpcRequestMutation} from '@aboutyou/data-fetcher';
import {CoinsService_SubmitMissionProgressLazy} from '@aboutyou/grpc-clients/tadarida/aysa_api/services/coins/v1/service.grpc.lazy';
import {
  SubmitMissionProgressRequest,
  SubmitMissionProgressResponse,
} from '@aboutyou/grpc-clients/tadarida/aysa_api/services/coins/v1/service.types';
import {useCustomerSession} from '@aboutyou/user';
import {useIsMissionsActive} from '../../../features/Coins/utils/useIsMissionsActive';
import {MissionType} from './missionType';
import {useGetActiveMissions} from './useGetActiveMissions';

export function useSubmitMissionProgress({
  mission,
}: {
  mission: 'likeBrandMission';
}): (brandId: number) => Promise<SubmitMissionProgressResponse>;

export function useSubmitMissionProgress({
  mission,
}: {
  mission: Exclude<MissionType, 'likeBrandMission'>;
}): () => Promise<SubmitMissionProgressResponse>;

export function useSubmitMissionProgress({mission}: {mission: MissionType}) {
  const customerSession = useCustomerSession();
  const isMissionsActive = useIsMissionsActive();
  const {refetch: refetchActiveMissions} = useGetActiveMissions();
  const disabled = !isMissionsActive || !customerSession;
  const emptyPromise = () =>
    Promise.resolve({} as SubmitMissionProgressResponse);

  const mutation = useGrpcRequestMutation(
    CoinsService_SubmitMissionProgressLazy,
    {
      disabled,
      onSuccess: () => refetchActiveMissions(),
    },
  );

  // disabled is not working on mutations, so we need to handle it manually
  if (disabled) {
    switch (mission) {
      case 'likeBrandMission':
        return async (_brandId: number) => emptyPromise();
      default:
        return async () => emptyPromise();
    }
  }

  switch (mission) {
    case 'likeBrandMission':
      return async (brandId: number) =>
        await mutation.mutateAsync({
          session: customerSession!,
          mission: {
            $case: 'likeBrandMission',
            likeBrandMission: {
              brandId,
            },
          },
        });
    default:
      return async () =>
        await mutation.mutateAsync({
          session: customerSession!,
          mission: {
            $case: mission,
            [mission]: {},
          } as unknown as NonNullable<SubmitMissionProgressRequest['mission']>,
        });
  }
}
